import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }
  
  #getVariantMediaMap() {
    const raw = this.dataset.variantMedia || '';
    const map = {};
    raw.split(',').forEach((pair) => {
      const [variantId, mediaId] = pair.split(':');
      if (variantId && mediaId) {
        map[variantId.trim()] = mediaId.trim();
      }
    });
    return map;
  }

  #getMediaIndex(mediaId) {
    const order = (this.dataset.mediaOrder || '').split(',').map((id) => id.trim());
    return order.indexOf(String(mediaId));
  }

  /**
   * Handles a variant update event.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    const variantId = event.detail.resource?.id;

    if (variantId) {
      const variantMediaMap = this.#getVariantMediaMap();
      const mediaId = variantMediaMap[String(variantId)];

      if (mediaId && mediaId !== 'undefined' && mediaId !== '') {
        const index = this.#getMediaIndex(mediaId);

        if (index !== -1) {
          this.refs.slideshow?.select(index, undefined, { animate: true });
          return;
        }
      }
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
